import { MappedField } from '@citruscamps/citrus-client'
import { faCloudUploadAlt } from '@fortawesome/pro-duotone-svg-icons'
import { faTimes } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FormikErrors, FormikTouched, useFormik } from 'formik'
import { default as React, useRef } from 'react'
import * as Yup from 'yup'
import { LoadingButton } from '../../../../../components/LoadingButton/LoadingButton'
import { useRouter } from '../../../../../hooks/useRouter'
import translations from '../../../../../translations/en.json'

const invalidFeedback = (
  key: keyof IValue,
  touched: FormikTouched<IValue>,
  errors: FormikErrors<IValue>,
) => (touched[key] && !!errors[key] ? errors[key] : null)

interface IValue {
  file?: File
}

interface IProps {
  initialValues?: IValue
  entityType: string
  onBack?: () => Promise<void> | void
  onSubmit: (values: MappedField[], file?: File) => Promise<void> | void
  onUploadFile?: (file: File) => MappedField[] | Promise<MappedField[]>
}

export const CsvImportForm = ({
  initialValues,
  entityType,
  onBack: handleBack,
  onUploadFile: handleUploadFile,
  onSubmit,
}: IProps): React.ReactElement => {
  const router = useRouter()
  const { errors, handleSubmit, isSubmitting, setFieldError, setFieldValue, touched, values } =
    useFormik<IValue>({
      initialValues: {
        file: initialValues?.file || undefined,
      },
      validationSchema: Yup.object().shape({
        file: Yup.mixed().required((translations as any)['error.default_inputs_message']),
      }),
      onSubmit: async (value, { setSubmitting }) => {
        try {
          const fields: MappedField[] = (value.file && (await handleUploadFile?.(value.file))) || []
          if (fields.length > 0) {
            if (!fields.some((f) => f.values.length > 500)) {
              await onSubmit(fields, value.file)
            } else {
              setFieldError(
                'file',
                (translations as any)['error.fields_entries_length_max']
                  .split('entries')
                  .join(entityType),
              )
            }
          } else if (fields.length === 0) {
            setFieldError('file', (translations as any)['error.fields_empty_file'])
          } else {
            setFieldError('file', (translations as any)['error.fields_invalid_file'])
          }
        } catch (e: any) {
          if (e.param) setFieldError(e.param, e.message)
        }
        setSubmitting(false)
      },
    })
  const inputRef = useRef<HTMLInputElement | null>(null)
  const baseUrl: string = process.env.REACT_APP_API_BASE_URL
    ? process.env.REACT_APP_API_BASE_URL
    : ''

  return (
    <form className="py-3" onSubmit={handleSubmit}>
      <h5 className="mb-4">Upload your {entityType}</h5>
      <div className="mb-3">
        <p>
          Import a file with your {entityType} information.{' '}
          <button
            type="button"
            className="btn btn-link p-0"
            onClick={() => {
              const link = document.createElement('a')
              link.download = ''
              link.target = '_blank'
              link.rel = 'noopener noreferrer'
              link.href = `${baseUrl}/api/v1/import/templates/${entityType}`
              document.body.appendChild(link)
              link.click()
              document.body.removeChild(link)
            }}
            style={{ verticalAlign: 'unset' }}
          >
            Download Template
          </button>
          .
        </p>
        <input
          ref={inputRef}
          type="file"
          id="csv"
          style={{ display: 'none' }}
          onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
            if (evt.target.files && evt.target.files.length > 0) {
              setFieldValue('file', evt.target.files[0], true)
            }
          }}
          accept=".csv, .xlsx"
        />
        <button
          type="button"
          className="btn btn-link btn-block d-md-inline w-md-auto"
          style={{ textTransform: 'unset' }}
        >
          <FontAwesomeIcon icon={faCloudUploadAlt} className="me-2" />
          <label className="mb-0" htmlFor="csv" style={{ cursor: 'pointer' }}>
            {values.file?.name && values.file?.name.length > 0 ? values.file.name : 'Upload CSV'}
          </label>
        </button>
        {values.file ? (
          <button
            type="button"
            className="btn btn-link btn-block d-md-inline w-md-auto text-danger"
            onClick={() => {
              if (inputRef.current) {
                inputRef.current.value = ''
              }
              onSubmit([])
              setFieldValue('file', undefined)
            }}
          >
            <FontAwesomeIcon icon={faTimes} className="me-2" />
          </button>
        ) : null}
        <div className="invalid-feedback d-block">
          {invalidFeedback('file', touched, errors) || '\u00A0'}
        </div>
      </div>
      <div className="mb-3 d-flex justify-content-between mt-4">
        {handleBack ? (
          <button
            type="button"
            className="btn btn-link btn-block d-md-inline-block w-md-auto mt-2 px-0"
            onClick={() => handleBack()}
          >
            Back
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-link btn-block d-md-inline-block w-md-auto mt-2 px-0"
            onClick={() => router.history.goBack()}
          >
            Cancel
          </button>
        )}
        <LoadingButton
          type="submit"
          className="btn btn-primary btn-block d-md-inline-block w-md-auto"
          loading={isSubmitting}
        >
          Continue
        </LoadingButton>
      </div>
    </form>
  )
}
